import { lazy, useEffect, useMemo, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { Loader } from '@nv/basic-components'
import { ListTable, useNVModal } from '@nv/components'
import { AdminVoterService } from '@nv/services'
import { formatDateLocaleShort } from '@nv/utils'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styles from './AdminEventElectorsList.module.scss'
const SuccessDownloaded = lazy(() => import('@nv/lazy-loadable/lib/modals/SuccessDownloaded'))
const Downloading = lazy(() => import('@nv/lazy-loadable/lib/modals/Downloading'))

const NUM_VOTERS_PER_PAGE = 10

export const AdminEventElectorsListPIPSC = () => {
  const { tiviId } = useParams()
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [currentPageNum, setCurrentPageNum] = useState(0)
  const [numRows, setNumRows] = useState(NUM_VOTERS_PER_PAGE)
  const [numUsers, setNumUsers] = useState(0)
  const { NVModal: SuccessDownloadedModal, openModal: openSuccessDownloadedModal } = useNVModal()
  const { NVModal: DownloadingModal, openModal: openDownloadingModal, closeModal: closeDownloadingModal } = useNVModal()

  function getAbbreviationByAccountType(accountType, user) {
    const groupDetails = user.userGroupsDetails.find((obj) => obj && obj.accountType === accountType)
    return groupDetails ? groupDetails.abbreviation[currentLanguage] : null
  }

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'username',
        cell: (info) => info.getValue() || '',
        header: () => t('auth.login.labels.username.voter'),
      },
      {
        accessorKey: 'fullName',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.fullName'),
      },
      {
        accessorKey: 'email',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.email'),
      },
      {
        id: 'Group',
        accessorKey: `group.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.group'),
      },
      {
        id: 'Subgroup',
        accessorKey: `subgroup.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.subgroup'),
      },
      {
        id: 'Region',
        accessorKey: `region.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.region'),
      },
      {
        id: 'Branch',
        accessorKey: `branch.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.branch'),
      },
      {
        accessorKey: 'userType',
        cell: (info) => (
          <span className={clsx(styles['type__label'], styles[`type__label--${info.getValue()?.toLowerCase()}`])}>
            {info.getValue() || ''}
          </span>
        ),
        header: () => t('filters.userFilters.type'),
      },
      {
        accessorKey: 'lastSignIn',
        cell: (info) => (info.getValue() ? formatDateLocaleShort(info.getValue().createdAt, currentLanguage) : ''),
        header: () => t('admin.electorsList.lastSignIn'),
      },
    ]
  }, [currentLanguage])

  const getUsersEvent = async () => {
    setLoading(true)
    const res = await AdminVoterService.getEventVoterList(tiviId, numRows, currentPageNum * numRows)
    if (res?.users) {
      setUsers(res.users)
      setNumUsers(res.numUsers)
    }
    setLoading(false)
  }

  useEffect(() => {
    getUsersEvent()
  }, [currentPageNum, numRows])

  const getDowloadUrl = async () => {
    openDownloadingModal()
    const res = await AdminVoterService.getEventVoterListCSV(tiviId, currentLanguage)
    if (res?.success) {
      // @ts-ignore
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.download = `voterlist`
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      closeDownloadingModal()
      openSuccessDownloadedModal()
    }
  }

  return (
    <>
      <DownloadingModal>
        <Downloading />
      </DownloadingModal>

      <SuccessDownloadedModal>
        <SuccessDownloaded />
      </SuccessDownloadedModal>

      <div className={styles['container']}>
        <h1 className={styles['header']}>{t('admin.electorsList.electorsList')}</h1>
        {!loading ? (
          <ListTable
            columns={columns}
            data={users}
            currentPageNum={currentPageNum}
            onPageChange={setCurrentPageNum}
            dataLength={numUsers}
            loading={loading}
            download={KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin() ? getDowloadUrl : null}
            paginationLabel={'admin.electorsList.electors'}
            numRowsPerPage={numRows}
            onNumRowsChange={(numRows) => setNumRows(numRows)}
          />
        ) : (
          <Loader />
        )}
      </div>
    </>
  )
}
